import { ChartData } from 'Views/LevelSensorOverview/hooks/useLevelSensorData';
import {
  eachWeekOfInterval,
  endOfDay,
  endOfWeek,
  getISOWeek,
  getISOWeekYear,
  startOfDay,
  startOfWeek,
  subDays,
  subWeeks,
} from 'date-fns';
import {
  getMaxCapacityCm,
  getMaxCapacityLiters,
  type TContainerType,
} from 'Views/LevelSensorOverview/containerTypes';

type UsageOpts = {
  start?: Date | number | string; // inclusive
  end?: Date | number | string; // inclusive
  toleranceCm?: number; // deadband to ignore noise
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6; // default Monday=1
  containerType?: TContainerType;
};

type DateRangeLike = {
  from?: Date | null;
  to?: Date | null;
};

export type InclusiveDateRange = {
  from: Date;
  to: Date;
};

export type NormalizedHistoryDateRanges = {
  selectedRange: InclusiveDateRange | null;
  weekSummaryRange: InclusiveDateRange | null;
  summaryFetchRange: InclusiveDateRange | null;
};

// assumes readings are sorted by time ascending
const toMs = (x: Date | number | string) =>
  x instanceof Date ? x.getTime() : typeof x === 'number' ? x : new Date(x).getTime();

export function normalizeHistoryDateRanges(
  range: DateRangeLike | undefined,
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 1
): NormalizedHistoryDateRanges {
  if (!range?.from || !range?.to) {
    return {
      selectedRange: null,
      weekSummaryRange: null,
      summaryFetchRange: null,
    };
  }

  const selectedRange = {
    from: startOfDay(range.from),
    to: endOfDay(range.to),
  };

  const weekSummaryRange = {
    from: startOfWeek(selectedRange.from, { weekStartsOn }),
    to: endOfWeek(selectedRange.to, { weekStartsOn }),
  };

  const summaryFetchRange = {
    // Seed the first summary week with enough history to find the last reading before Monday,
    // even when the device was quiet for several days before the selected week started.
    from: startOfDay(subDays(subWeeks(weekSummaryRange.from, 1), 1)),
    to: weekSummaryRange.to,
  };

  return {
    selectedRange,
    weekSummaryRange,
    summaryFetchRange,
  };
}

export function isDateWithinInclusiveRange(
  value: Date,
  range: InclusiveDateRange | null | undefined
): boolean {
  if (!range) return false;

  const time = value.getTime();
  return time >= range.from.getTime() && time <= range.to.getTime();
}

export function totalUsageCm(
  readings: ChartData[],
  opts: { start?: Date | number | string; end?: Date | number | string; toleranceCm?: number } = {}
): number {
  const s = opts.start !== undefined ? toMs(opts.start) : -Infinity;
  const e = opts.end !== undefined ? toMs(opts.end) : Infinity;
  const tol = opts.toleranceCm ?? 0.5;

  let total = 0;
  let last: number | undefined;

  for (const r of readings) {
    const t = toMs(r.rawTimestamp);
    if (t < s) {
      last = r.level;
      continue;
    }
    if (t > e) break;

    if (last !== undefined) {
      const drop = last - r.level;
      if (drop > tol) total += drop;
    }
    last = r.level;
  }
  return total;
}

export type WeeklyUsage = {
  week: Date; // e.g. "2025-W33" (ISO)
  weekStart: Date;
  weekEnd: Date;
  usageCm: number;
  usageLiters: number;
};

export function getIsoWeekSummary(date: Date) {
  return {
    week: getISOWeek(date),
    year: getISOWeekYear(date),
  };
}

export function usageByWeek(readings: ChartData[], opts: UsageOpts = {}): WeeklyUsage[] {
  if (readings.length < 2) return [];
  if (!opts.containerType) return [];
  const maxCapacityCm = getMaxCapacityCm(opts.containerType);
  const maxCapacityLiters = getMaxCapacityLiters(opts.containerType);
  const weekStartsOn = opts.weekStartsOn ?? 1;

  const selectedStart =
    opts.start !== undefined
      ? startOfDay(new Date(toMs(opts.start)))
      : startOfDay(new Date(toMs(readings[0].rawTimestamp)));
  const selectedEnd =
    opts.end !== undefined
      ? endOfDay(new Date(toMs(opts.end)))
      : endOfDay(new Date(toMs(readings[readings.length - 1].rawTimestamp)));
  if (selectedStart >= selectedEnd) return [];

  const alignedStart = startOfWeek(selectedStart, { weekStartsOn });
  const alignedEnd = endOfWeek(selectedEnd, { weekStartsOn });

  const anchors = eachWeekOfInterval({ start: alignedStart, end: alignedEnd }, { weekStartsOn });

  const out: WeeklyUsage[] = [];
  for (const ws of anchors) {
    const weekStart = startOfWeek(ws, { weekStartsOn });
    const weekEnd = endOfWeek(ws, { weekStartsOn });

    const usage = totalUsageCm(readings, {
      start: weekStart,
      end: weekEnd,
      toleranceCm: opts.toleranceCm,
    });

    out.push({
      week: weekStart,
      weekStart,
      weekEnd,
      usageCm: usage,
      usageLiters: (usage / maxCapacityCm) * maxCapacityLiters,
    });
  }
  return out;
}
